import { useRedirectLogOutUser } from "../../../customHook/useRedirectLogOutUser";
import { useMemo, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTable, usePagination, useGlobalFilter, useFilters } from "react-table";
import { GlobalFilter } from "../../globalFilter";
import ReactToPrint from "react-to-print";
import { SelectColumnFilter, multiSelectFilter } from "./SelectColumnFilter1";
import { getCumulativeSales } from "../../../redux/features/sales/saleSlice";
import { EthiopianDateCell } from '../EthiopianDateCell';

const dash = (v) => (v === null || v === undefined || v === "") ? "—" : v;

export const CumulativeSales = () => {
  useRedirectLogOutUser();

  const dispatch    = useDispatch();
  const printRef    = useRef();
  const [printView, setPrintView] = useState(false);

  useEffect(() => {
    dispatch(getCumulativeSales());
  }, [dispatch]);

  const { cumulativeSales = [] } = useSelector((state) => state.sale);

  // ── Columns — accessors match backend aggregate output ──────────────────
  const columns = useMemo(() => [
    {
      Header: "No",
      id: "no",
      Cell: ({ row }) => row.index + 1,
    },
    {
      Header: "Product Name",
      accessor: "product.name",
      Filter: SelectColumnFilter,
      filter: "multiSelectFilter",
      Cell: ({ value }) => dash(value),
    },
    {
      Header: "SKU",
      accessor: "product.sku",
      Filter: SelectColumnFilter,
      filter: "multiSelectFilter",
      Cell: ({ value }) => dash(value),
    },
    {
      Header: "Total Qty Sold",
      accessor: "totalQtySold",
      Cell: ({ value }) => value != null ? value.toLocaleString() : "—",
    },
    {
      Header: "Total Sale Amount",
      accessor: "totalSaleAmount",
      Cell: ({ value }) => value != null
        ? <span className="fw-semibold">{value.toLocaleString()} birr</span>
        : "—",
    },
    {
      Header: "Total Profit",
      accessor: "totalProfit",
      Cell: ({ value }) => value != null
        ? <span className="text-success fw-semibold">{value.toLocaleString()} birr</span>
        : "—",
    },
    {
      Header: "Profit Margin",
      accessor: "profitMarginPct",
      Cell: ({ value }) => {
        if (value == null) return "—";
        const pct = parseFloat(value).toFixed(1);
        const color = pct >= 20 ? "success" : pct >= 10 ? "warning" : "danger";
        return (
          <span className={`badge bg-${color} rounded-pill px-2 py-1`}>
            {pct}%
          </span>
        );
      },
    },
    {
      Header: "Min Sold Price",
      accessor: "minSoldPrice",
      Cell: ({ value }) => value != null ? `${value.toLocaleString()} birr` : "—",
    },
    {
      Header: "Max Sold Price",
      accessor: "maxSoldPrice",
      Cell: ({ value }) => value != null ? `${value.toLocaleString()} birr` : "—",
    },
    {
      Header: "Avg Sold Price",
      accessor: "avgSoldPrice",
      Cell: ({ value }) => value != null
        ? `${Math.round(value).toLocaleString()} birr`
        : "—",
    },
    {
      Header: "Transactions",
      accessor: "transactionCount",
      Cell: ({ value }) => value != null ? value.toLocaleString() : "—",
    },
    {
      Header: "First Sold",
      accessor: "firstSoldDate",
      Cell: ({ value }) => value ? <EthiopianDateCell value={value} dual /> : "—"

    },
    {
      Header: "Last Sold",
      accessor: "lastSoldDate",
      Filter: SelectColumnFilter,
      filter: "multiSelectFilter",
      Cell: ({ value }) => value ? <EthiopianDateCell value={value} dual /> : "—",
    },
  ], []);

  const data        = useMemo(() => Array.isArray(cumulativeSales) ? cumulativeSales : [], [cumulativeSales]);
  const filterTypes = useMemo(() => ({ multiSelectFilter }), []);

  const tableInstance = useTable(
    { columns, data, filterTypes, defaultColumn: { Filter: () => null } },
    useFilters, useGlobalFilter, usePagination
  );

  const {
    getTableProps, getTableBodyProps, headerGroups,
    page, rows,
    nextPage, previousPage, canNextPage, canPreviousPage,
    gotoPage, pageCount, pageOptions,
    setGlobalFilter, setPageSize,
    state: { pageIndex, globalFilter, pageSize },
    prepareRow,
  } = tableInstance;

  // ── Subtotal calculations ────────────────────────────────────────────────
  const subtotals = useMemo(() => ({
    totalQtySold:    rows.reduce((s, r) => s + (r.original.totalQtySold    || 0), 0),
    totalSaleAmount: rows.reduce((s, r) => s + (r.original.totalSaleAmount || 0), 0),
    totalProfit:     rows.reduce((s, r) => s + (r.original.totalProfit     || 0), 0),
  }), [rows]);

  return (
    <div>

      {/* ── Header bar ────────────────────────────────────────────────────── */}
      <div className="d-flex justify-content-between align-items-center m-4 px-4 py-2 rounded-4 border"
        style={{ backgroundColor: "#faf8f5" }}>
        <h4 className="mb-0">Cumulative Sales</h4>
        <ReactToPrint
          trigger={() => (
            <button className="btn btn-primary rounded-3" onClick={() => setPrintView(true)}>
              Print Table
            </button>
          )}
          content={() => printRef.current}
          onAfterPrint={() => setPrintView(false)}
          onBeforeGetContent={async () => setPrintView(true)}
          pageStyle="@page { size: A4 landscape; margin: 10mm; }"
        />
      </div>

      {/* ── Search ────────────────────────────────────────────────────────── */}
      <div className="px-4 mb-3">
        <div className="px-4 rounded-4 border" style={{ backgroundColor: "#faf8f5" }}>
          <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} />
        </div>
      </div>

      {/* ── Summary cards ─────────────────────────────────────────────────── */}
      <div className="px-4 mb-3" style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: "1rem",
      }}>
        <div className="rounded-4 border p-3" style={{ backgroundColor: "#faf8f5" }}>
          <p className="text-muted mb-1" style={{ fontSize: "0.78rem", fontWeight: 600 }}>TOTAL QTY SOLD</p>
          <p className="fw-bold fs-5 mb-0">{subtotals.totalQtySold.toLocaleString()}</p>
        </div>
        <div className="rounded-4 border p-3" style={{ backgroundColor: "#faf8f5" }}>
          <p className="text-muted mb-1" style={{ fontSize: "0.78rem", fontWeight: 600 }}>TOTAL SALE AMOUNT</p>
          <p className="fw-bold fs-5 mb-0 text-primary">{subtotals.totalSaleAmount.toLocaleString()} birr</p>
        </div>
        <div className="rounded-4 border p-3" style={{ backgroundColor: "#faf8f5" }}>
          <p className="text-muted mb-1" style={{ fontSize: "0.78rem", fontWeight: 600 }}>TOTAL PROFIT</p>
          <p className="fw-bold fs-5 mb-0 text-success">{subtotals.totalProfit.toLocaleString()} birr</p>
        </div>
        <div className="rounded-4 border p-3" style={{ backgroundColor: "#faf8f5" }}>
          <p className="text-muted mb-1" style={{ fontSize: "0.78rem", fontWeight: 600 }}>PRODUCTS SOLD</p>
          <p className="fw-bold fs-5 mb-0">{data.length}</p>
        </div>
      </div>

      {/* ── Table ─────────────────────────────────────────────────────────── */}
      <div className="px-4" ref={printRef}>
        {printView && (
          <>
            <div style={{ padding: "1.5rem" }}><h2>Cumulative Sales Table</h2></div>
            <div style={{ marginBottom: "1rem", textAlign: "right", fontSize: "1.5rem" }}>
              Date: {new Date().toLocaleDateString()}
            </div>
          </>
        )}

        <div className="table-responsive rounded-4 border" style={{ backgroundColor: "#fff" }}>
          <table className="table table-striped table-hover caption-top mb-0"
            {...getTableProps()} style={{ fontSize: "12px", minWidth: "100%" }}>
            <thead>
              {headerGroups.map(headerGroup => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => (
                    <th className="px-3" style={{ backgroundColor: "#faf8f5" }}
                      {...column.getHeaderProps()}>
                      {column.render("Header")}
                      {!printView && column.canFilter && (
                        <div>{column.render("Filter")}</div>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody {...getTableBodyProps()}>
              {page.map(row => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map(cell => (
                      <td className="px-3" {...cell.getCellProps()}>
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })}

              {page.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="text-center text-muted py-4">
                    No cumulative sales data found.
                  </td>
                </tr>
              )}
            </tbody>

            {/* ── Subtotal footer (based on ALL filtered rows, not just current page) */}
            <tfoot>
              <tr style={{ backgroundColor: "#e8f4ff", fontWeight: 700 }}>
                <td className="px-3 py-2" colSpan={4}>Subtotal ({rows.length} products)</td>
                <td className="px-3 py-2">{subtotals.totalSaleAmount.toLocaleString()} birr</td>
                <td className="px-3 py-2 text-success">{subtotals.totalProfit.toLocaleString()} birr</td>
                <td className="px-3 py-2" colSpan={7}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* ── Pagination ────────────────────────────────────────────────────── */}
      <div className="mt-3">
        <ul className="pagination justify-content-center pagination-lg">
          <span style={{ padding: "5px", fontSize: "15px" }}>
            page <strong><em style={{ color: "blue" }}>{pageIndex + 1}</em> of {pageOptions.length}</strong>
          </span>
          <select className="form-select form-select-lg" style={{ maxWidth: "90px" }}
            value={pageSize} onChange={e => setPageSize(Number(e.target.value))}>
            {[10, 15, 20, 30, 40, 50, data.length].map((s, i) => (
              <option key={i} value={s}>Show {s}</option>
            ))}
          </select>
          <li><button className="page-link" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>{"<<"}</button></li>
          <li className={`page-item ${!canPreviousPage ? "disabled" : ""}`}>
            <button className="page-link" onClick={previousPage} disabled={!canPreviousPage}>Previous</button>
          </li>
          <li className={`page-item ${!canNextPage ? "disabled" : ""}`}>
            <button className="page-link" onClick={nextPage} disabled={!canNextPage}>Next</button>
          </li>
          <li><button className="page-link" onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>{">>"}</button></li>
        </ul>
      </div>

    </div>
  );
};